import { React, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme, useMediaQuery } from '@mui/material';
import { useAtom } from "jotai";

import focusBlk from '../../../assets/images/icons/focusBlk.svg';

import "./listing.scss"
import { ScrollToTopOnMount } from "../../../Components/ScrollToTop/ScrollToTop";
import CallPopup from "../../../Components/ContactPopup/CallPopup";
import { getProperties } from "../../../services/api/property";
import { showSnackbar } from '../../../Components/Notification/Snackbar';

import { initialPropertyState, propertyState } from "../../../state/property.state";
import SVGIcons from "../../../assets/images/icons/svgIcons";
import PropertyListGridView from "./propertyListGridView";
import FilterForm from "../../../Components/homeRevamp/filterForm/filterForm";
import { formURLSlug, getUniqueId } from "../../../helpers/helper.functions";
import SliderThumbPage from "./SliderThumbPage";
import S3ImageViewerWithFallback from "../../../Components/awsS3ImageViewer/ImgViewerWithFallback";


/**
 * @description - this is a listing page for all properties. It uses react-router
* to navigate between pages and also has some basic routing logic in place
 */
const Listing = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = params.get('mode');

  const [propertyListKey, setPropertyListKey] = useState('property-list');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [propertyStateAtom, setPropertyStateAtom] = useAtom(propertyState);
  const [listView, setListView] = useState('grid');
  const [isListRent, setIsListRent] = useState(false);


  ScrollToTopOnMount();

  // Set filters and load properties based on mode
  useEffect(() => {
    const loadData = async () => {
      let filter = null;

      switch (mode) {
        case 'buy':
          filter = { ...initialPropertyState.filter, operationType: 'sell', limit: 1000, page: 1 };
          setListView('list');
          setIsListRent(false);
          break;
        case 'rent':
          filter = { ...initialPropertyState.filter, operationType: 'rent', limit: 1000, page: 1 };
          setIsListRent(true);
          setListView('list');
          break;
        case 'off-plan':
          filter = { ...initialPropertyState.filter, completionStatus: 'inprogress', limit: 1000, page: 1 };
          break;
        case 'filtered':
          // use existing atom filter
          filter = { ...propertyStateAtom.filter, limit: 1000, page: 1 };
          break;
        default:
          filter = { limit: 1000, page: 1 };
          break;
      }

      setPropertyStateAtom(prev => ({ ...prev, filter }));
      setIsLoading(true);

      try {
        const response = await getProperties(filter);
        setData(response.data);
      } catch (error) {
        showSnackbar().failure('Something went wrong');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line
  }, [mode]);

  const getPropertiesAs = (isShowFiltered = false) => {
    const filter = isShowFiltered
      ? { ...propertyStateAtom.filter, limit: 1000, page: 1 }
      : { limit: 1000, page: 1 };

    setIsLoading(true);
    getProperties(filter)
      .then(({ data }) => setData(data))
      .catch(() => showSnackbar().failure('Something went wrong'))
      .finally(() => setIsLoading(false))
  }
  const generateSlug = (title) => formURLSlug(title);

  const clearFilter = () => {
    getPropertiesAs(false);
    setPropertyListKey(`${getUniqueId()}-new-filter-list`);
    setPropertyStateAtom({ ...propertyStateAtom, filter: { ...initialPropertyState.filter } });
  };

  const handleGridView = () => setListView('grid');
  const handleListView = () => setListView('list');

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);

  const list = data || [];
  const totalPages = Math.ceil(list.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = list.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (e) => {
    setCurrentPage(Number(e.target.value));
    ScrollToTopOnMount();
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }

  };

  const handleBack = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div id='app-main-content' className="main-content-wrapper listing-wrapper listing-wrapper-revamp f-wrp">

      {/* <div className="filter-belt f-wrp">
        <div className="container">
          <FilterForm formKey={propertyListKey} onFilter={() => getPropertiesAs(true)} />
        </div>
      </div> */}


      <div className='main-banner-wrp inner-banner f-wrp'>
        {/* <div className='banner-bg f-wrp'>
          <img src={AppIcons.homeBannerBG} alt='buildings' />
        </div> */}

        <div className='filter-box f-wrp'>
          <div className="container">
            <div className='filter-form'>
              <FilterForm formKey={propertyListKey} onFilter={() => getPropertiesAs(true)} />
            </div>
          </div>
        </div>


      </div>
      {/* <div className="breadcrumb f-wrp">
        <div className="container">
          <Link to={"/"}><img src={BackArrow} alt="back-arrow" />Home</Link>
        </div>
      </div> */}
      <div className="list-main-wrapper list-wrapper-revamp f-wrp">
        <div className="container">

          <div className='value-header listViewBtn f-wrp'>
            {isListRent ? <h1>Properties for Rent in Dubai</h1> : <h1>Properties for Sale in Dubai</h1>}

            {!isSmallScreen ? <div className="list-view-switch">
              {mode !== null ?
                <button disabled={isLoading} style={{ width: 'max-content' }} onClick={clearFilter}>
                  Clear filter
                </button>
                : null}
              <button
                onClick={handleListView}
                aria-pressed={listView === 'list'}
                className={listView === 'list' ? 'active' : ''}
              >
                <SVGIcons.ListViewIcon />
              </button>
              <button
                onClick={handleGridView}
                aria-pressed={listView === 'grid'}
                className={listView === 'grid' ? 'active' : ''}
              >
                <SVGIcons.GridViewIcon />
              </button>

            </div> : null}

          </div>

          <div className="list-con-sec f-wrp">
            {(isLoading) ? <span className='property-loader'> <SVGIcons.LoaderIcon /></span> : ''}
            {(!isLoading && data.length === 0) ? <span style={{ textAlign: 'center', display: 'block' }}>{"No Results found"} </span> : ''}

            {(!isLoading) ? <>

              {!isSmallScreen ? <>
                {(listView === 'grid') ?
                  <div className="propertyList-gridView-wrp f-wrp">
                    <PropertyListGridView propertyListArr={data || []} />
                  </div> : null}
                {(listView === 'list') ?
                  <div className="propertyListing-listView f-wrp">
                    {data.map((element, key) => (
                      <>
                        <div className="each-list-blk f-wrp" key={key}>

                          <Link className="absolute-btn" to={`/property-detail/${generateSlug(element.titleSlug)}`} ></Link>
                          <div className="img-gallery-wrp f-wrp">
                            {/* {(element.images.length > 0) && <ListThumbSlider isHovered={isHovered === key} images={element.images.filter((i) => i.tempURL).map((img) => img.tempURL)} thumbnails={(element.thumbnails.length > 0) ? element.thumbnails.filter((i) => i && i.tempURL).map((img) => img?.tempURL) : element.images.filter((i) => i && i.tempURL).map((img) => img?.tempURL)} />} */}

                            {element?.images?.length ? <SliderThumbPage images={element?.images ? element?.images.map(img => img.tempURL) : []}
                              thumbnails={(element?.thumbnails > 0) ? element?.thumbnails.filter((i) => i && i.tempURL).map(img => img.tempURL) : element?.images.filter((i) => i && i.tempURL).map(img => img.tempURL)} /> : null}
                          </div>
                          <div className="list-cont-sec f-wrp">
                            <div className="prop-detail-wrp f-wrp">
                              <div className="each-prop-detail-box">
                                {element?.title && <h1 className="title">{element?.title}</h1>}
                                {element?.subTitle && <h1 className="subTitle">{element?.subTitle}</h1>}
                                {element?.price && <h1 className="price">{element?.price} AED</h1>}
                                {element.location && <h3 className="location"><SVGIcons.MapPinIcon /> &nbsp; {element.location}</h3>}

                                <ul className="flex-list basic-detail-list f-wrp">
                                  {element?.noOfBedrooms &&
                                    <li>
                                      <span className="list-icon"><SVGIcons.BedIcon /></span> Beds: {element?.noOfBedrooms}
                                    </li>}
                                  {element?.noOfBathrooms &&
                                    <li>
                                      <span className="list-icon"><SVGIcons.BathIcon /></span> Baths: {element?.noOfBathrooms}
                                    </li>}
                                  {element?.bua &&
                                    <li>
                                      <span className="list-icon"><img src={focusBlk} alt="focus-icon" /></span> BUA: {element?.bua} sft
                                    </li>}
                                  {element?.area &&
                                    <li>
                                      <span className="list-icon"><SVGIcons.AreaIcon /></span> Area: {element?.area} {element?.areaMeasureUnit}
                                    </li>}
                                </ul>

                                {element.permitQRCode ? <span className='absolute-qr-code'><S3ImageViewerWithFallback fileKey={element.permitQRCode} /></span> : null}
                              </div>


                              <div className="each-prop-detail-box">
                                <div className="eachlist-contact-sec f-wrp">
                                  <ul>
                                    <li>
                                      <div className="contct-btn-sec f-wrp">
                                        <CallPopup />
                                      </div>
                                    </li>
                                    <li>
                                      <div className="contct-btn-sec f-wrp">
                                        <div className="whstapp-btn" onClick={() => window.open('https://wa.me/971568816886', '_blank')}><SVGIcons.WhatsappIcon /><span>Whatsapp</span></div>
                                      </div>
                                    </li>
                                    <li>
                                      <div className="contct-btn-sec f-wrp">
                                        <div className="whstapp-btn" onClick={() => window.open("https://wa.me/971568816886?text=Book%20a%20Viewing", '_blank')}><SVGIcons.WhatsappIcon /><span>Book a Viewing</span></div>
                                      </div>
                                    </li>
                                  </ul>
                                </div>
                              </div>




                              {/* <h1 className="prop-label">{element.title} <b>AED {element.price}</b></h1>
                            <div className="mob-price">{element.location && <span className="location"><img src={MapPinBlk} alt="map-pin" />{element.location}</span>}
                              <b><i>AED</i> {element.price}</b></div>
                            <ul className="flex-list f-wrp">
                              {element.area &&
                                <li>
                                  <span className="list-icon"><img src={ArrowsOutBlk} alt="arrowout-icon" /></span>
                                  <p>Area: <b>{element.area} sft</b></p>
                                </li>}
                              {element.bua &&
                                <li>
                                  <span className="list-icon"><img src={focusBlk} alt="focus-icon" /></span>
                                  <p>BUA: <b>{element.bua}</b></p>
                                </li>}
                              
                            </ul>
                            <div className={`split feature-list f-wrp`}>
                              {element.features && <>
                                <label>Features:</label>
                                <div>
                                  <RichTextViewer richText={element.features} />
                                </div>
                              </>}
                            </div> */}


                            </div>
                          </div>
                        </div>
                        {/* <div className="eachlist-contact-sec f-wrp">
                        <ul>
                          <li>
                            <div className="contct-btn-sec f-wrp">
                              <CallPopup />
                            </div>
                          </li>
                          <li>
                            <div className="contct-btn-sec f-wrp">
                              <div className="whstapp-btn" onClick={() => window.open('https://wa.me/971568816886', '_blank')}><img src={whatsap} alt="whatsapp" /><span>WhatsApp</span></div>
                            </div>
                          </li>
                          <li>
                            <div className="contct-btn-sec inquiry f-wrp">
                              <ContactPopup />
                            </div>
                          </li>
                        </ul>
                      </div> */}
                      </>
                    ))}
                  </div> : null}
              </> : <>
                <div className="propertyListing-listView mobile-list f-wrp">
                  {currentItems.map((element, key) => (
                    <>
                      <div className="each-list-blk f-wrp" key={key}>
                        <Link className="absolute-btn" to={`/property-detail/${generateSlug(element.titleSlug)}`} ></Link>
                        <div className="img-gallery-wrp f-wrp">
                          {element?.images?.length ? <SliderThumbPage images={element?.images ? element?.images.map(img => img.tempURL) : []}
                            thumbnails={(element?.thumbnails > 0) ? element?.thumbnails.filter((i) => i && i.tempURL).map(img => img.tempURL) : element?.images.filter((i) => i && i.tempURL).map(img => img.tempURL)} /> : null}

                          {element.permitQRCode ? <span className='absolute-qr-code'><S3ImageViewerWithFallback fileKey={element.permitQRCode} /></span> : null}

                        </div>
                        <div className="list-cont-sec f-wrp">
                          <div className="prop-detail-wrp f-wrp">
                            <div className="each-prop-detail-box">
                              {element?.title && <h1 className="title">{element?.title}</h1>}
                              {element?.subTitle && <h1 className="subTitle">{element?.subTitle}</h1>}
                              {element?.price && <h1 className="price">{element?.price} AED</h1>}
                              {element.location && <h3 className="location"><SVGIcons.MapPinIcon /> &nbsp; {element.location}</h3>}
                              <ul className="flex-list basic-detail-list f-wrp">
                                {element?.noOfBedrooms &&
                                  <li>
                                    <span className="list-icon"><SVGIcons.BedIcon /></span> Beds: {element?.noOfBedrooms}
                                  </li>}
                                {element?.noOfBathrooms &&
                                  <li>
                                    <span className="list-icon"><SVGIcons.BathIcon /></span> Baths: {element?.noOfBathrooms}
                                  </li>}
                                {element?.bua &&
                                  <li>
                                    <span className="list-icon"><img src={focusBlk} alt="focus-icon" /></span> BUA: {element?.bua} sft
                                  </li>}
                                {element?.area &&
                                  <li>
                                    <span className="list-icon"><SVGIcons.AreaIcon /></span> Area: {element?.area} {element?.areaMeasureUnit}
                                  </li>}
                              </ul>
                            </div>
                            <div className="each-prop-detail-box">
                              <div className="eachlist-contact-sec f-wrp">
                                <ul>
                                  <li><div className="contct-btn-sec f-wrp"><CallPopup /></div></li>
                                  <li><div className="contct-btn-sec f-wrp">
                                    <div className="whstapp-btn" onClick={() => window.open('https://wa.me/971568816886', '_blank')}><SVGIcons.WhatsappIcon /><span>Whatsapp</span></div>
                                  </div></li>
                                  <li><div className="contct-btn-sec f-wrp">
                                    <div className="whstapp-btn" onClick={() => window.open("https://wa.me/971568816886?text=Book%20a%20Viewing", '_blank')}><SVGIcons.WhatsappIcon /><span>Book a Viewing</span></div>
                                  </div></li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ))}
                </div>
                <div className="list-pagination f-wrp">
                  {list.length > itemsPerPage && (
                    <>
                      <button onClick={handleBack} disabled={currentPage === 1}><SVGIcons.LeftIcon /> Back</button>
                      <span>
                        Page{' '}
                        <select
                          value={currentPage}
                          onChange={handlePageChange}
                          style={{ padding: '2px 6px' }}
                        >
                          {Array.from({ length: totalPages }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </select>{' '}
                        of {totalPages}
                      </span>
                      <button onClick={handleNext} disabled={currentPage === totalPages}>Next <SVGIcons.RightIcon /></button>
                    </>
                  )}
                </div>

              </>
              }

            </> : null}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Listing;
